import {
  HttpClient,
  httpResource,
  HttpResourceRef,
} from '@angular/common/http';
import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, from, map, merge, of, switchMap, Observable } from 'rxjs';
import {
  AuthSessionService,
  OidcFacadeService,
  tenantConfig$,
  toCurrentUserFromIntrospection,
} from '@mbai/multi-tenant-common-auth-ng';
import { environment } from '../../../../environments/environment';
import { ICurrentUser } from '../../../core/models/current-user.model';
import type { IUserManagementRes } from '../../../core/models/user-management-res.model';
import { AppResourceStatus } from '../../../core/enums/app-resource-status';
import type { IUser } from '../models/user.model';
import type { IAddUserRequest } from '../models/add-user-request.model';
import type { IEditUserRequest } from '../models/edit-user-request.model';
import type { IUserFilter } from '../models/filter-user.model';
import type { ICommonListData } from '../models/list-data.model';
import { getQueryParams } from '../utils/query-params.util';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly oidc = inject(OidcFacadeService);
  private readonly authSession = inject(AuthSessionService);
  private readonly httpClient = inject(HttpClient);
  private readonly apiBaseUrl = environment.userManagementAPI;
  private readonly tenantConfig = toSignal(tenantConfig$, { initialValue: undefined });

  readonly realm = computed(() => this.tenantConfig()?.realm ?? null);

  readonly accessToken = toSignal(
    merge(
      of(null as string | null),
      this.oidc.isAuthenticated$().pipe(
        switchMap(({ isAuthenticated }) =>
          isAuthenticated ? from(this.oidc.getAccessToken()) : of(null),
        ),
        catchError(() => of(null)),
      ),
    ),
    { initialValue: null },
  );

  readonly currentUser = toSignal(
    merge(
      of(null as ICurrentUser | null),
      this.oidc.isAuthenticated$().pipe(
        switchMap(({ isAuthenticated }) =>
          isAuthenticated ? from(this.oidc.getAccessTokenClaims()) : of(null),
        ),
        map((claims) => toCurrentUserFromIntrospection(claims, this.authSession.userEmail)),
        catchError(() => of(null)),
      ),
    ),
    { initialValue: null },
  );

  private readonly userResource = httpResource<IUserManagementRes<{ roles: { id: string; displayName: string }[] }>>(() => {
    const config = this.tenantConfig();
    if (!config || !this.accessToken()) {
      return undefined;
    }
    return `${this.apiBaseUrl}/v3/realms/${config.realm}/me?client=${config.clientId}`;
  });

  readonly isMeLoading = computed(() => this.userResource.isLoading());

  readonly user = computed<{ roles: { id: string; displayName: string }[] } | undefined>(() => {
    if (this.userResource.status() === AppResourceStatus.Resolved) {
      return this.userResource.value()?.data;
    }
    return undefined;
  });

  getUsers(
    filter: Signal<IUserFilter>,
  ): HttpResourceRef<IUserManagementRes<ICommonListData<IUser>> | undefined> {
    return httpResource<IUserManagementRes<ICommonListData<IUser>>>(() => {
      const realm = this.realm();
      if (!realm || !this.accessToken()) {
        return undefined;
      }
      return `${this.apiBaseUrl}/v3/realms/${realm}/users?${getQueryParams(filter())}`;
    });
  }

  getUsersPage(
    filter: IUserFilter,
  ): Observable<IUserManagementRes<ICommonListData<IUser>>> {
    const realm = this.realm();
    if (!realm || !this.accessToken()) {
      return of({ data: { total: 0, records: [] } });
    }
    return this.httpClient.get<IUserManagementRes<ICommonListData<IUser>>>(
      `${this.apiBaseUrl}/v3/realms/${realm}/users?${getQueryParams(filter)}`,
    );
  }

  addUser(request: IAddUserRequest): Observable<IUserManagementRes<object>> {
    return this.httpClient.post<IUserManagementRes<object>>(
      `${this.apiBaseUrl}/v3/realms/${this.realm()}/users`,
      request,
    );
  }

  editUser(id: string, request: IEditUserRequest): Observable<IUserManagementRes<object>> {
    return this.httpClient.put<IUserManagementRes<object>>(
      `${this.apiBaseUrl}/v3/realms/${this.realm()}/users/${id}`,
      request,
    );
  }

  deleteUser(id: string): Observable<IUserManagementRes<object>> {
    return this.httpClient.delete<IUserManagementRes<object>>(
      `${this.apiBaseUrl}/v3/realms/${this.realm()}/users/${id}`,
    );
  }
}
